// Catalog layer — derives slugs + 4-collection split (Books / Apparel / Studio / Originals)
// from the existing PRODUCTS data without mutating products.js
import { PRODUCTS, PAYMENT_URL } from './products';

export { PAYMENT_URL };

// Manual slug overrides for the books (these URLs are linked from josephgravesjr.com)
const SLUG_OVERRIDES = {
  'cb-001': 'daddy-joes-cowboy-capers',
  'cb-002': 'you-are-an-artist',
};

// Legacy slugs that were renamed — redirect old bookmarks to the new URL.
export const LEGACY_SLUG_MAP = {
  'maverick': 'mile-high-maverick',
  'freedoms-touch': 'oh-beautiful',
  'look-at-me': 'made-u-look',
};

// Old category id -> new collection slug
const COLLECTION_MAP = {
  'childrens-books': 'books',
  'apparel': 'apparel',
  'limited-edition-prints': 'studio',
  'original-paintings': 'originals', // split out — gallery treatment
  'paper-goods': 'studio',
  'prints': 'studio',
};

// Apparel size matrix — which products are sized vs not (hats are one-size)
export const APPAREL_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];
const SIZED_APPAREL_IDS = new Set(['ap-001', 'ap-002']); // hoodie + tee
export const requiresSize = (product) =>
  product?.collection === 'apparel' && SIZED_APPAREL_IDS.has(product.id);

const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const COLLECTIONS = [
  {
    slug: 'books',
    name: 'Books',
    tagline: 'Picture books for big and little hearts.',
    blurb:
      'Hand-illustrated picture books written and painted by Joseph Graves Jr. — signed copies shipped from the studio.',
    feature_image:
      'https://customer-assets.emergentagent.com/job_9cdf2342-a607-473f-8553-b1e4a60c73ce/artifacts/7g18ogrj_Cowboy%20capers.jpg',
  },
  {
    slug: 'apparel',
    name: 'Apparel',
    tagline: 'Wearable art from the studio.',
    blurb:
      'Hoodies, tees, and trucker hats featuring original artwork. Heavyweight cotton, built to last.',
    feature_image:
      'https://customer-assets.emergentagent.com/job_gallerystore/artifacts/2g4unqd4_Screenshot%202026-01-14%20at%209.43.22%20PM.png',
  },
  {
    slug: 'originals',
    name: 'Originals',
    tagline: 'One-of-one paintings from $1,100. Flagship works by inquiry.',
    blurb:
      'Original mixed-media works on canvas — each signed and accompanied by a certificate of authenticity. Inquire to acquire.',
    feature_image:
      'https://customer-assets.emergentagent.com/job_gallerystore/artifacts/5rsgdx79_Graves%2C%20Joseph%2C%20AWsample2.JPG',
  },
  {
    slug: 'studio',
    name: 'Prints',
    tagline: 'Signed prints, paper goods, and small-batch editions.',
    blurb:
      'Limited edition prints, open editions, and small studio goods — directly from the artist\u2019s table.',
    feature_image:
      'https://customer-assets.emergentagent.com/job_9cdf2342-a607-473f-8553-b1e4a60c73ce/artifacts/7by1lzch_Sunflower%20Between%20Us.JPG',
  },
];

// Build the unified product list
const buildCatalog = () => {
  const all = [];
  const usedSlugs = new Set();
  Object.entries(PRODUCTS).forEach(([catId, items]) => {
    items.forEach((p) => {
      let slug = SLUG_OVERRIDES[p.id] || slugify(p.title);
      let suffix = 1;
      let candidate = slug;
      while (usedSlugs.has(candidate)) {
        suffix += 1;
        candidate = `${slug}-${suffix}`;
      }
      slug = candidate;
      usedSlugs.add(slug);
      all.push({
        ...p,
        slug,
        collection: COLLECTION_MAP[catId] || 'studio',
        originalCategory: catId,
      });
    });
  });
  return all;
};

export const ALL_PRODUCTS = buildCatalog();

export const getProductsByCollection = (collectionSlug) =>
  ALL_PRODUCTS.filter((p) => p.collection === collectionSlug);

export const getProductBySlug = (slug) =>
  ALL_PRODUCTS.find((p) => p.slug === slug);

export const getCollection = (slug) =>
  COLLECTIONS.find((c) => c.slug === slug);
